// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/state-machines/connectivity-utils.ts
// Parameters       : none — below this header, identical across the fleet
// Destination      : packages/fleet/state-machines/src/connectivity-utils.ts
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo jornada-activity-app
//     node fleet/fleet.ts check --repo jornada-activity-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
import { pausesWrites } from "@fleet/maintenance";
import type {
  ConnectivityContext,
  ConnectivityDescriptionKeys,
  ConnectivityLabelKey,
  ConnectivityStateName,
} from "./connectivity-machine";

/**
 * Is the CURRENT window pausing writes while the connection is otherwise fine?
 *
 * Reads the machine context (not the module singleton) so this stays a pure
 * function of the snapshot — the UI renders from the machine, and mixing the two
 * sources is how an indicator ends up one poll behind what the dialog says.
 */
export function isWritePausedContext(context: ConnectivityContext): boolean {
  return pausesWrites({
    active: context.maintenanceScopes.length > 0,
    scopes: context.maintenanceScopes,
    message: context.maintenanceMessage,
    until: null,
  });
}

/**
 * Should the user be TOLD about a write pause?
 *
 * Only when the window carries a message. `message: null` is a silent window on
 * purpose — the operator decided the user does not need to know, and an
 * offline-first client has nothing to show anyway (reads work, writes queue).
 */
export function shouldAnnounceWritePause(context: ConnectivityContext): boolean {
  return isWritePausedContext(context) && context.maintenanceMessage !== null;
}

/**
 * Logic-heavy mapping from state/context to i18n keys.
 */
export function getConnectivityDescriptionKeys(
  labelKey: ConnectivityLabelKey,
  context: ConnectivityContext,
): ConnectivityDescriptionKeys {
  switch (labelKey) {
    case "offline": {
      return {
        titleKey: "connectivity.offline.title",
        messageKey: "connectivity.offline.message",
        confirmTextKey: "connectivity.actions.check_again",
      };
    }
    case "reconnecting": {
      return {
        titleKey: "connectivity.reconnecting.title",
        messageKey: "connectivity.reconnecting.message",
        confirmTextKey: "connectivity.actions.check_again",
      };
    }
    case "maintenance": {
      return {
        titleKey: "connectivity.maintenance.title",
        messageKey: context.maintenanceMessage
          ? "connectivity.maintenance.custom_message"
          : "connectivity.maintenance.message",
        confirmTextKey: "connectivity.actions.check_again",
      };
    }
    case "unauthenticated": {
      return {
        titleKey: "connectivity.unauthenticated.title",
        messageKey: "connectivity.unauthenticated.message",
        confirmTextKey: "connectivity.actions.sign_in",
        actionType: "auth",
      };
    }
    case "checking": {
      return {
        titleKey: "connectivity.checking.title",
        messageKey: "connectivity.checking.message",
        hideConfirm: true,
        cancelTextKey: "connectivity.actions.close",
      };
    }
    case "connected": {
      // A `writes`-scoped maintenance window arrives with `apiStatus: "up"`, so
      // the machine stays `connected` — deliberately: the user keeps reading
      // their local data and downloads keep flowing, so a blocking dialog would
      // be wildly out of proportion. It surfaces here instead, and ONLY when the
      // window carries a message (`message: null` = SILENT window: nothing is
      // shown at all, the queued-changes copy below covers it honestly).
      if (shouldAnnounceWritePause(context)) {
        return {
          titleKey: "connectivity.maintenance_writes.title",
          messageKey: "connectivity.maintenance_writes.custom_message",
          confirmTextKey: "connectivity.actions.ok",
        };
      }
      if (context.isUploading) {
        return {
          titleKey: "connectivity.syncing.title",
          messageKey: "connectivity.syncing.message",
          confirmTextKey: "connectivity.actions.ok",
        };
      }
      if (context.uploadQueueSize > 0) {
        return {
          titleKey: "connectivity.pending.title",
          messageKey: "connectivity.pending.message",
          confirmTextKey: "connectivity.actions.sync_now",
        };
      }
      return {
        titleKey: "connectivity.connected.title",
        messageKey: "connectivity.connected.message",
        confirmTextKey: "connectivity.actions.check_again",
      };
    }
    default: {
      return {
        titleKey: "connectivity.connected.title",
        messageKey: "connectivity.connected.message",
        confirmTextKey: "connectivity.actions.check_again",
      };
    }
  }
}

/**
 * Diagnostic helpers for UI status mapping.
 *
 * INDEPENDENCE INVARIANT (see debug page): the API indicator reflects ONLY the
 * API subsystem (`context.apiStatus`, fed by the `/api/system/health` poller)
 * and the Sync indicator reflects ONLY the PowerSync subsystem
 * (`context.syncStatus`). They are ORTHOGONAL: a PowerSync failure must never
 * drag the API indicator off green, and an API failure must never drag the Sync
 * indicator. The ONLY shared precondition is the network (`offline`) — without
 * it neither subsystem is reachable — which is itself surfaced as its own
 * dedicated row. Crucially, these helpers must NOT key off the SHARED macro
 * states (`checking` / `manualChecking` / `reconnecting`): those blend both
 * subsystems, so reading them here is exactly what coupled the two indicators
 * (the API row went yellow merely because PowerSync was still verifying).
 */
export function getApiStatusLabelKey(
  matches: (stateValue: ConnectivityStateName) => boolean,
  apiStatus: string | undefined,
  /** Scopes of the current window, so a write-only pause is named as such. */
  maintenanceScopes: readonly string[] = [],
): string {
  if (matches("offline")) {
    return "Waiting for Network";
  }
  if (apiStatus === "maintenance") {
    return "Maintenance Mode";
  }
  // `writes`-only: the API is genuinely up (reads work), so calling it
  // "Maintenance Mode" would overstate it — but saying "Operational" hides the
  // reason the user's changes are queueing.
  if (maintenanceScopes.length > 0) {
    return "Maintenance: writes paused";
  }
  if (apiStatus === "down") {
    return "Service Unreachable";
  }
  if (apiStatus === "checking") {
    return "Verifying Connection...";
  }
  // `up` (or the initial default before the first health probe lands).
  return "Operational";
}

export function getApiStatusCategory(
  matches: (stateValue: ConnectivityStateName) => boolean,
  apiStatus: string | undefined,
  maintenanceScopes: readonly string[] = [],
): "success" | "warning" | "danger" | "neutral" {
  if (matches("offline")) {
    return "neutral";
  }
  if (apiStatus === "maintenance" || apiStatus === "checking") {
    return "warning";
  }
  if (maintenanceScopes.length > 0) {
    return "warning";
  }
  if (apiStatus === "down") {
    return "danger";
  }
  // `up` (or the initial default before the first health probe lands).
  return "success";
}

export function getSyncStatusLabelKey(
  matches: (stateValue: ConnectivityStateName) => boolean,
  syncStatus: string | undefined,
): string {
  if (matches("offline")) {
    return "Waiting for Network";
  }
  // A guest/visitor never syncs — auth is a genuine precondition for sync, and
  // is orthogonal to the API (the API can be perfectly up for a guest).
  if (matches("unauthenticated")) {
    return "Visitor Mode";
  }
  if (syncStatus === "connected") {
    return "Connected";
  }
  if (syncStatus === "connecting") {
    return "Connecting...";
  }
  if (syncStatus === "error") {
    return "Error";
  }
  return "Disconnected";
}

export function getSyncStatusCategory(
  matches: (stateValue: ConnectivityStateName) => boolean,
  syncStatus: string | undefined,
): "success" | "warning" | "danger" | "neutral" {
  if (matches("offline") || matches("unauthenticated")) {
    return "neutral";
  }
  if (syncStatus === "connecting") {
    return "warning";
  }
  return syncStatus === "connected" ? "success" : "danger";
}
